import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import { DEFAULT_CURRENCY_CODE, enableProdMode, LOCALE_ID } from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { NoPreloading, provideRouter, withPreloading, withViewTransitions } from '@angular/router';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { CACHE_KEYS } from '@scheduler-frontend/cache-keys';
import { provideInitializeStore } from '@scheduler-frontend/data-access-initialize';
import { provideUsersStore } from '@scheduler-frontend/data-access-users';
import { environment } from '@scheduler-frontend/environments';
import { checkUserRoles } from '@scheduler-frontend/permissions';
import {
  provideClearCacheOnMajorUpgrade,
  provideIonicInitialization,
} from '@scheduler-frontend/providers';
import { jsonLdInterceptor, TtPreloadingStrategy } from '@techniek-team/common';
import { provideTtFeatureFlags } from '@techniek-team/feature-flags';
import { provideMercureClient } from '@techniek-team/mercure-client';
import {
  authWithSecureRoutesInterceptor,
  LyceoUser,
  provideTtAuth,
  UserService,
  withClearStorageOnImpersonator,
  withLocalStorage,
  withUserRoles,
} from '@techniek-team/oauth';
import { PermissionService, provideTtPermissions } from '@techniek-team/permissions';
import {
  provideSentry,
  SentryEnvironment,
  SentryWebConfig,
  withTracing,
  withUserFeedback,
} from '@techniek-team/sentry-web';
import { Drivers, provideStorage } from '@techniek-team/storage';
import { setDefaultOptions } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MainComponent } from './main.component';
import { ROUTES } from './main.routing';

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeNl, 'nl');
setDefaultOptions({
  locale: nl,
  weekStartsOn: 1,
});

await bootstrapApplication(MainComponent, {
  providers: [
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: MAT_DATE_LOCALE, useValue: nl },
    provideStorage({
      name: '__scheduler-frontend',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
      tables: [
        'application',
        CACHE_KEYS.ngrxCache,
        CACHE_KEYS.ngrxCacheSchedules,
        CACHE_KEYS.ngrxCacheAwaitingFirstSlots,
        CACHE_KEYS.ngrxCacheAwaitingFollowUpSlots,
        CACHE_KEYS.ngrxCacheSelectedProductTypesAtLocation,
      ],
    }),
    provideIonicAngular({
      useSetInputAPI: true,
    }),
    provideSignalFormsConfig({
      classes: {
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-valid': ({ state }) => state().valid(),
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-invalid': ({ state }) => state().invalid(),
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-touched': ({ state }) => state().touched(),
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-dirty': ({ state }) => state().dirty(),
      },
    }),
    provideClearCacheOnMajorUpgrade('__scheduler-frontend'),
    provideHttpClient(withInterceptors([jsonLdInterceptor, authWithSecureRoutesInterceptor])),
    provideRouter(
      ROUTES,
      withPreloading(environment.production ? TtPreloadingStrategy : NoPreloading),
      withViewTransitions(),
    ),
    provideDateFnsAdapter(),
    provideSentry(
      {
        environment: environment.environment,
        dsn: 'https://31bab22356a0415494b67b7b5a5493ef@errors.techniek-team.nl//31',
        release: `nl.lyceo.roostertool@${environment.release}`,
        autoSessionTracking: true,
        enableTracing: true,
        profileLifecycle: 'trace',
        tracePropagationTargets: [
          'localhost',
          `${environment.scheduler.url}/${environment.scheduler.iri}`,
          `${environment.perza.url}/${environment.perza.iri}`,
          environment.mercure.hub,
        ],
        userService: UserService<LyceoUser>,
        ignoreErrors: ['Handled unknown error'],
      } as SentryWebConfig,
      withTracing(),
      withUserFeedback({
        colorScheme: 'system',
        autoInject: false,
        useSentryUser: {
          name: 'username',
          email: 'email',
        },
      }),
    ),
    provideTtPermissions({
      roleHierarchy: {
        ROLE_SKOLEO_TT_PLANNER_ADMIN: [
          'admin',
          'ROLE_SKOLEO_TT_PLANNER_LOCATION_MANAGEMENT',
          'ROLE_SKOLEO_TT_PLANNER_CLUSTER_MANAGEMENT',
          'ROLE_SKOLEO_TT_PLANNER_SCHEDULING_TEAM',
        ],
        ROLE_SKOLEO_TT_PLANNER_CLUSTER_MANAGEMENT: 'clusterManager',
        ROLE_SKOLEO_TT_PLANNER_LOCATION_MANAGEMENT: 'locationManager',
        ROLE_SKOLEO_TT_PLANNER_SCHEDULING_TEAM: 'schedulingTeam',
      },
      userService: UserService,
    }),
    provideTtAuth(
      {
        scope: 'openid profile email',
        authority: environment.ssoConfig.authority,
        redirectUrl: environment.ssoConfig.redirectUri,
        postLogoutRedirectUri: `${environment.ssoConfig.redirectUri}/login`,
        unauthorizedRoute: '/login',
        forbiddenRoute: '/geen-toegang',
        maxIdTokenIatOffsetAllowedInSeconds: 300,
        triggerRefreshWhenIdTokenExpired: true,
        clientId: environment.ssoConfig.clientId,
        secureRoutes: [
          `${environment.scheduler.url}${environment.scheduler.iri}`,
          `${environment.perza.url}${environment.perza.iri}`,
        ],
        disableRefreshIdTokenAuthTimeValidation: true,
      },
      withLocalStorage('__scheduler-frontend'),
      withUserRoles(checkUserRoles, [PermissionService]),
      withClearStorageOnImpersonator(),
    ),
    provideTtFeatureFlags({
      url: environment.gitlabFeatureFlags.url,
      appName: 'skoleo-scheduler-front-end',
    }),
    provideMercureClient({
      debug: false,
      hubUrl: environment.mercure.hub,
      tokenEndpointUrl: environment.mercure.tokenUrl,
    }),
    provideStore(
      {
        '[bootstrap] router': routerReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
          strictStateSerializability: false,
          strictActionSerializability: false,
          strictActionWithinNgZone: false,
          strictActionTypeUniqueness: false,
        },
      },
    ),
    provideRouterStore(),
    environment.environment !== SentryEnvironment.PRODUCTION
      ? provideStoreDevtools({
          name: 'skoleo-scheduler-front-end',
          maxAge: 100, // Retains last 100 states
          logOnly: environment.environment !== SentryEnvironment.LOCAL, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
          trace: true, //environment.environment === SentryEnvironment.LOCAL,
          traceLimit: 75,
          connectInZone: false,
        })
      : [],
    { provide: LOCALE_ID, useValue: 'nl' },
    provideInitializeStore(),
    provideUsersStore(),
    provideIonicInitialization(),
  ],
})
  //eslint-disable-next-line no-console
  .catch((err: unknown) => console.log(err));
